import { useState } from 'react'
import { eachDayOfInterval, format, parseISO } from 'date-fns'
import {
  keepPreviousData,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import {
  CREDIT_KIND_CLASSES,
  CREDIT_KIND_LABELS,
  CREDIT_POOLS,
  formatCredits,
  formatKindLabel,
} from '@/lib/credits'
import type { CreditPool } from '@/lib/credits'
import { creditUsage } from '@/lib/queries/credit-usage'
import type { CreditUsageAggregates } from '@/lib/queries/credit-usage'
import { organization } from '@/lib/queries/organizations'
import { CategoryBarChart } from '@/components/charts/category-bar-chart'
import { MetricCard } from '@/components/charts/metric-card'
import { TimeSeriesChart } from '@/components/charts/time-series-chart'
import type { TimeSeries } from '@/components/charts/time-series-chart'
import { DateRangePicker } from '@/components/date-range-picker'

type StrictDateRange = { from: Date; to: Date }

type DailyPoint = { date: string } & Record<CreditPool, number>

const creditSeries: TimeSeries<DailyPoint>[] = CREDIT_POOLS.map((pool) => ({
  dataKey: pool,
  label: CREDIT_KIND_LABELS[pool].short,
  color: CREDIT_KIND_CLASSES[pool].fill,
}))

export const Route = createFileRoute('/_authenticated/org/$orgId/usage/')({
  // The default range is the current billing period, so the charts' "N in
  // period" IS this org's usage against its monthly credits without a separate
  // headline. The period comes from get_usage, resolved before render.
  loader: async ({ context, params }) => {
    const org = await context.queryClient.query({
      ...organization.findOne(params.orgId),
      staleTime: 'static',
    })
    await context.queryClient.query({
      ...creditUsage.balance(org.billing_account_id),
      staleTime: 'static',
    })
  },
  component: OrgUsagePage,
})

/** The billing period, clipped to today: future days would only pad the axis. */
function billingPeriodRange(periodStart: string, periodEnd: string) {
  const today = new Date()
  const end = new Date(periodEnd)
  return { from: new Date(periodStart), to: end > today ? today : end }
}

function transformAggregates(
  data: CreditUsageAggregates,
  dateRange: StrictDateRange
) {
  // Every day in range, so the x-axis has no holes on quiet days.
  const allDays = eachDayOfInterval({
    start: dateRange.from,
    end: dateRange.to,
  })

  const dailyCredits = new Map<string, DailyPoint>()
  allDays.forEach((day) => {
    const key = format(day, 'yyyy-MM-dd')
    dailyCredits.set(key, {
      date: format(day, 'MMM d'),
      allowance: 0,
      wallet: 0,
    })
  })
  data.entries.forEach((entry) => {
    const point = dailyCredits.get(
      format(parseISO(entry.occurredAt), 'yyyy-MM-dd')
    )
    if (point) {
      point[entry.pool] += entry.credits
    }
  })

  return {
    totalCredits: data.totalCredits,
    dailySeries: Array.from(dailyCredits.values()),
    typeBreakdown: data.typeBreakdown,
    workspaceBreakdown: data.workspaceBreakdown,
  }
}

function CreditKindLegend() {
  return (
    <ul className='text-muted-foreground flex flex-wrap gap-4 ps-6 text-xs sm:ps-0'>
      {CREDIT_POOLS.map((pool) => (
        <li key={pool} className='flex items-center gap-1.5'>
          <span
            aria-hidden
            className={`size-2 rounded-full ${CREDIT_KIND_CLASSES[pool].bg}`}
          />
          {CREDIT_KIND_LABELS[pool].long}
        </li>
      ))}
    </ul>
  )
}

function OrgUsagePage() {
  const { orgId } = Route.useParams()
  const { data: org } = useSuspenseQuery(organization.findOne(orgId))
  const { data: balance } = useSuspenseQuery(
    creditUsage.balance(org.billing_account_id)
  )
  const periodRange = billingPeriodRange(balance.periodStart, balance.periodEnd)
  const [dateRange, setDateRange] = useState<StrictDateRange>(periodRange)

  const startDate = format(dateRange.from, 'yyyy-MM-dd')
  const endDate = format(dateRange.to, 'yyyy-MM-dd')

  const { data, isFetching } = useQuery({
    ...creditUsage.aggregateByOrg(orgId, startDate, endDate),
    placeholderData: keepPreviousData,
  })

  const aggregated = data
    ? transformAggregates(data, dateRange)
    : {
        totalCredits: 0,
        dailySeries: [],
        typeBreakdown: [],
        workspaceBreakdown: [],
      }

  // The tooltip already says "Credits:" — the value is just the number.
  const creditTooltip = (v: number) => formatCredits(v)

  return (
    <div className='w-full space-y-8'>
      <div className='mb-6 flex flex-wrap items-center justify-between gap-4'>
        <div>
          <h1 className='font-heading text-3xl tracking-tight'>Usage</h1>
          <p className='text-muted-foreground'>
            Credits consumed by this organization
          </p>
        </div>
        <div className='flex items-center gap-2'>
          {isFetching && (
            <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
          )}
          <DateRangePicker
            value={dateRange}
            extraPresets={[{ label: 'Period', range: periodRange }]}
            onChange={(range) => {
              if (range?.from && range?.to) {
                setDateRange({ from: range.from, to: range.to })
              }
            }}
          />
        </div>
      </div>

      <div className='grid gap-4 lg:grid-cols-2'>
        <MetricCard
          title='Credits over time'
          metric={`${formatCredits(aggregated.totalCredits)} in period`}
          description='Daily credit consumption for this organization'
        >
          <div className='flex h-full flex-col gap-3'>
            <CreditKindLegend />
            <div className='min-h-0 flex-1'>
              <TimeSeriesChart
                data={aggregated.dailySeries}
                series={creditSeries}
                type='area'
                tooltipFormatter={creditTooltip}
              />
            </div>
          </div>
        </MetricCard>

        <MetricCard
          title='Credits by product area'
          description='What consumed the credits in this period'
        >
          {aggregated.typeBreakdown.length === 0 ? (
            <div className='text-muted-foreground flex h-full items-center justify-center text-sm'>
              No credits consumed in this period
            </div>
          ) : (
            <CategoryBarChart
              data={aggregated.typeBreakdown}
              categoryKey='label'
              valueKey='credits'
              tooltipLabel='Credits'
              tooltipFormatter={creditTooltip}
              tooltipDetails={(t) =>
                // Every kind that says more than the bar label already does —
                // so a single "Creation: 14" under "Images: 14" still shows,
                // while Video's only kind ("Video") is left out as noise.
                t.kinds
                  .filter((k) => formatKindLabel(k.kind) !== t.label)
                  .map(
                    (k) =>
                      `${formatKindLabel(k.kind)}: ${formatCredits(k.credits)}`
                  )
              }
            />
          )}
        </MetricCard>
      </div>

      {aggregated.workspaceBreakdown.length > 0 && (
        <MetricCard
          title='Usage by workspace'
          description='Top workspaces by credits consumed'
        >
          <CategoryBarChart
            data={aggregated.workspaceBreakdown}
            categoryKey='workspace'
            valueKey='credits'
            tooltipLabel='Credits'
            tooltipFormatter={creditTooltip}
          />
        </MetricCard>
      )}
    </div>
  )
}
